import { useCallback, useEffect, useState } from 'react';
import {
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import FolderIcon from '@material-ui/icons/Folder';
import {
  getStaticExternalPagePermission,
  StaticExternalPage,
} from './useStaticExternalPageGroups';
import { externalPagePath } from './staticExternalPagesPlugin';

type VisibilityChange = (pageId: string, visible: boolean) => void;
type PermissionFor = (pageId: string) => BasicPermission;
type PathFor = (pageId: string) => string;

/**
 * Renders nothing itself -- exists only to run usePermission (SWR-backed,
 * revalidates on window focus/reconnect) for one page and report the result
 * up to ExternalPageSubmenuGroup. Kept mounted regardless of whether the
 * group header is currently shown, so a page that becomes newly allowed
 * (or newly denied) is picked up without a full page reload.
 */
function ExternalPageAccessProbe({
  page,
  getPermission,
  onVisibilityChange,
}: {
  page: StaticExternalPage;
  getPermission: PermissionFor;
  onVisibilityChange: VisibilityChange;
}) {
  const { loading, allowed } = usePermission({
    permission: getPermission(page.uid),
  });

  useEffect(() => {
    if (loading) return;
    onVisibilityChange(page.uid, allowed);
  }, [loading, allowed, page.uid, onVisibilityChange]);

  return null;
}

/**
 * One "folder" in the sidebar's static-content area for a static-external-page
 * submenu. Each candidate page's access is checked independently (gating is
 * per-page, not per-submenu -- see ElitePermissionPolicy), and the whole
 * group hides itself when none of its pages are currently visible, rather
 * than showing an empty, expandable folder.
 */
export function ExternalPageSubmenuGroup({
  submenuLabel,
  pages,
  getPermission = getStaticExternalPagePermission,
  getPath = externalPagePath,
}: {
  submenuLabel: string;
  pages: StaticExternalPage[];
  /** Defaults to the static-external-page permission; hosted apps pass their own. */
  getPermission?: PermissionFor;
  /** Defaults to /external-pages/:id; hosted apps pass their own. */
  getPath?: PathFor;
}) {
  const [visibleIds, setVisibleIds] = useState<Set<string>>(() => new Set());

  const onVisibilityChange = useCallback<VisibilityChange>((pageId, visible) => {
    setVisibleIds(prev => {
      if (visible === prev.has(pageId)) return prev;
      const next = new Set(prev);
      if (visible) {
        next.add(pageId);
      } else {
        next.delete(pageId);
      }
      return next;
    });
  }, []);

  const visiblePages = pages.filter(page => visibleIds.has(page.uid));

  return (
    <>
      {pages.map(page => (
        <ExternalPageAccessProbe
          key={page.uid}
          page={page}
          getPermission={getPermission}
          onVisibilityChange={onVisibilityChange}
        />
      ))}
      {visiblePages.length > 0 && (
        <SidebarItem
          icon={FolderIcon}
          text={submenuLabel}
          to={getPath(visiblePages[0].uid)}
        >
          <SidebarSubmenu title={submenuLabel}>
            {visiblePages.map(page => (
              <SidebarSubmenuItem
                key={page.uid}
                title={page.title}
                to={getPath(page.uid)}
              />
            ))}
          </SidebarSubmenu>
        </SidebarItem>
      )}
    </>
  );
}
